import { useToast } from "@chakra-ui/react";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import api from "@/common/utils/api";
import { companyQueryKeys } from "@/company/data/companyQueryKeys";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import { ErrorResponse } from "@reflag/shared/api";
import { FeatureDetail, PatchFeatureArgsType } from "@reflag/shared/featureAPI";

type PatchFeatureArgsTypeWithId = PatchFeatureArgsType & {
  id: string;
};

export function useFeatureUpdateMutation(
  companyId?: string,
  options: Omit<
    UseMutationOptions<
      FeatureDetail,
      AxiosError<ErrorResponse>,
      PatchFeatureArgsTypeWithId
    >,
    "mutationKey"
  > = {},
) {
  const { appId, envId } = useEnvironment();

  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options;

  return useMutation<
    FeatureDetail,
    AxiosError<ErrorResponse>,
    PatchFeatureArgsTypeWithId
  >({
    mutationKey: ["feature"],

    mutationFn: async (data) => {
      const { id: featureId, ...updatedData } = data;
      return api
        .patch<"/apps/:appId/features/:featureId">(
          `/apps/${appId}/features/${featureId}`,
          updatedData,
          { params: { envId: envId! } },
        )
        .then((res) => res.data.feature);
    },

    onSuccess: (updatedFeature, data, context) => {
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.listNames(appId),
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.list(appId, envId),
      });
      queryClient.invalidateQueries({
        queryKey: featureQueryKeys.single(appId, envId, updatedFeature.id),
      });
      // Also invalidate flags query for tree view updates
      queryClient.invalidateQueries({
        queryKey: ["apps", appId, "flags"],
      });
      if (companyId) {
        queryClient.invalidateQueries({
          queryKey: companyQueryKeys.flags(appId, envId, companyId),
        });
      }
      queryClient.setQueryData(
        featureQueryKeys.single(appId, envId, updatedFeature.id),
        updatedFeature,
      );

      onSuccess?.(updatedFeature, data, context);
    },

    ...otherOptions,
  });
}

export function useArchiveFeatureMutation(
  options: Omit<
    UseMutationOptions<
      FeatureDetail,
      AxiosError<ErrorResponse>,
      PatchFeatureArgsTypeWithId
    >,
    "mutationKey" | "mutationFn"
  > = {},
  unarchive = false,
) {
  const toast = useToast();
  const errorToast = useErrorToast();

  const { onSuccess, onError } = options;
  const updateMutation = useFeatureUpdateMutation(undefined, {
    onSuccess: async (updatedFeature, data, context) => {
      toast({
        title: !unarchive ? "Flag archived" : "Flag unarchived",
        status: "success",
      });
      onSuccess?.(updatedFeature, data, context);
    },
    onError: (updatedFeature, data, context) => {
      errorToast({
        title: !unarchive
          ? "Couldn't archive the flag"
          : "Couldn't unarchive the flag",
      });

      onError?.(updatedFeature, data, context);
    },
  });

  return updateMutation;
}

export function useArchiveFeature(featureId: string) {
  const mutation = useArchiveFeatureMutation();

  return () => mutation.mutate({ id: featureId, isArchived: true });
}

export function useUnArchiveFeature(featureId: string) {
  const mutation = useArchiveFeatureMutation(undefined, true);

  return () => mutation.mutate({ id: featureId, isArchived: false });
}
